import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Injector,
    OnDestroy,
    OnInit,
    Renderer2,
    DOCUMENT,
    Type,
    afterNextRender,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { BannerTop, HeaderMode, SiteHeaderService } from './site-header.service';
import { ScrollingService } from '../scrolling.service';
import { Subscription } from 'rxjs';
import { GoogleTextAdService, AdsType } from '../content/google-text-ad/google-text-ad.service';
import { BreakPointSizeService } from '../core/services/break-point-size/break-point-size.service';
import { NavigationEnd, Router, Event } from '@angular/router';
import { AppGlobalsModule } from '../app-globals.module';
import { AngularPages, SiteLocations, UrlAddressService } from '../core/services/url-address/url-address.service';
import { FilterBoxService } from '../shared/filter/filter-box.service';
import { debounceTime, distinctUntilChanged, filter, take } from 'rxjs/operators';
import { SocialShareService } from '../shared/social-share/social-share.service';
import { UtilService } from '../core/services/util/util.service';

import { I18nService } from '../core/services/i18n/i18n.service';
import { CalendarService, DailyTimeHeader, ModeTime } from '../calendar/calendar-service/calendar.service';
import { BroadcastNotificationService } from '../core/services/broadcast-notification/broadcast-notification.service';
import { BrowserPlatformService } from '../core/services/browser-platform/browser-platform.service';
import { isAdFreeUrl, isNihulSurfaceUrl } from '../nihul/nihul-surfaces';
@Component({
    selector: 'site-header-ng',
    templateUrl: './site-header.component.html',
    styleUrls: ['./site-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SiteHeaderComponent implements OnInit, OnDestroy, AfterViewInit {
    private _siteHeaderService = inject(SiteHeaderService);
    private _scrollingService = inject(ScrollingService);
    breakPointSizeService = inject(BreakPointSizeService);
    socialShareService = inject(SocialShareService);
    urlAddress = inject(UrlAddressService);
    private _util = inject(UtilService);
    i18n = inject(I18nService);
    private _calendarService = inject(CalendarService);
    private _filterBoxService = inject(FilterBoxService);
    adService = inject(GoogleTextAdService, { optional: true })!;
    private _router = inject(Router);
    private changeDetector = inject(ChangeDetectorRef);
    private _injector = inject(Injector);
    private _doc = inject<Document>(DOCUMENT, { optional: true })!;
    broadcastNotificationService = inject(BroadcastNotificationService);
    private _renderer2 = inject(Renderer2);
    private _browser = inject(BrowserPlatformService);

    // TODOs:
    //    User summary bar
    public globals = AppGlobalsModule;
    public AngularPages = AngularPages;
    public activeAngularPage = signal<AngularPages | undefined>(undefined);
    public hebrewDate = signal(this._util.getHebrewCalander());
    public dailyDonor = signal(this._siteHeaderService.getEmptyDailyDonor());
    public omer = signal('');
    public bannerTop = signal<BannerTop | undefined>(undefined);

    public isFirstScreen = signal(true);
    public FocusSearch = signal(false);
    public isAtTop = signal(true);
    public isServer = AppGlobalsModule.isServer;
    public isAdLoaded = signal(false);
    public headerMode = signal<HeaderMode | undefined>(undefined);
    public currAdTopType = signal<AdsType | undefined>(undefined);
    readonly LEPTOP_INNER_HEIGHT: number = 620;
    readonly MINI_MOBILE_HEIGHT: number = 400;
    // TODO: implement redirect logic based on routing URL
    // @ViewChild('animateAd') animation!: ElementRef;

    // public animation = document.querySelector('section.animate');
    public isHomeContentArea = signal(false);
    public isMainMenuOpen = signal(false);
    /** the nihul (internal tool) shows no ads - internal users' work time beats the ad income */
    public isNihulPage = signal(false);
    /** a public page without ads (the store's order flow) - the ads go, nothing else changes */
    public isAdFreePage = signal(false);
    public isEnableAdStickyFooter = signal(false);
    public isAngularApp = signal(false);
    public isAdTopEnable = signal(true);
    public isAdBottomMobileEnable = signal(false);
    isBroadCastNotificationEnable = signal(false);
    isClosingStickyFooterAd = signal(false);
    broadcastNotificationComponent = signal<Type<unknown> | null>(null);
    readonly broadcastNotificationInputs = {
        closeHandler: (isClose: boolean) => this.broadcastNotificationClosed(isClose),
    };
    socialShareBoxComponent = signal<Type<unknown> | null>(null);
    socialShareBoxModule = signal<Type<unknown> | null>(null);
    private _getIsAdTopLoadedTopSub: Subscription | undefined;
    private _setHeaderStickyTopSub: Subscription = Subscription.EMPTY;
    private _subscriptions: Subscription[] = [];
    private _hasCompletedInitialHeaderSync = false;
    private _hasQueuedInitialHeaderSync = false;
    private _hasQueuedHeaderStickyTopSync = false;
    private _isDestroyed = false;
    private _hasSeenBroadcastNotificationNavigation = false;
    // Do not directly use it with string comparison since it's ineffective.
    public activeLocation = signal<SiteLocations | undefined>(undefined);
    public SiteLocations = SiteLocations;

    public dailyTimeHeader = signal<DailyTimeHeader | undefined>(undefined);
    public isTopAdAboveScreen = signal(false);

    public isHeaderFixed = computed(() => (this.isTopAdAboveScreen() || !this.isAdLoaded()) && this.headerMode() !== HeaderMode.Static);
    public showPadding = computed(() => !this.isAtTop());
    //the explicit nihul gate is required - !isAdLoaded() alone would re-show the empty ad wrapper there
    public showAdTop = computed(() => !this.isNihulPage() && !this.isAdFreePage() && (!this.isAdLoaded() || this.isAdTopEnable()));

    constructor() {
        effect(() => {
            this.breakPointSizeService.currBreakpoint();
            if (this._hasCompletedInitialHeaderSync && this.isAdTopEnable()) {
                // Breakpoint changes can swap the ad wrapper size; measure after Angular renders the new layout.
                this._queueHeaderStickyTopSync();
            }
        });
    }

    ngOnInit(): void {
        // In server it's already been initiated manually so observable won't work.
        if (this.urlAddress.activeAngularPage) {
            this.activeAngularPage.set(this.urlAddress.activeAngularPage);
        }

        //seed from the real address - the header lives outside the routed content and the router
        //hasn't navigated yet on a hard load (on the server this is the generic esi fragment url)
        this._updateIsNihulPage(this._browser.location?.pathname ?? '');
        this._subscriptions.push(
            this._router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
                this._updateIsNihulPage(event.urlAfterRedirects || event.url);
                const isInitialNavigation = !this._hasSeenBroadcastNotificationNavigation;
                this._hasSeenBroadcastNotificationNavigation = true;

                void this._showBroadcastNotificationPromptIfNeeded(isInitialNavigation);
            }),
        );
    }

    private static _isNihulUrl(url: string): boolean {
        //the SHARED surfaces list (nihul-surfaces.ts), not a local /nihul test: a nihul page at
        //its own public address (/hofeshlimud/manage) counted as a public page here, so the
        //every-N-navigations broadcast prompt kept firing over the editor's screen - the very
        //bug the /nihul guard was written for
        return isNihulSurfaceUrl(url);
    }

    /** the store's order flow: the ads already showing go off on entry; playAds() skips these urls, so nothing comes back until the customer leaves */
    private _updateIsAdFreePage(url: string): void {
        const adFree = isAdFreeUrl(url);
        if (adFree === this.isAdFreePage()) {
            return;
        }
        this.isAdFreePage.set(adFree);
        if (adFree) {
            this.adService.setIsEnableAdStickyFooter(false);
            this.adService.setIsAdTopEnable(false);
        }
        this.changeDetector.markForCheck();
    }

    private _updateIsNihulPage(url: string): void {
        this._updateIsAdFreePage(url);
        const isNihul = SiteHeaderComponent._isNihulUrl(url);
        if (isNihul === this.isNihulPage()) {
            return;
        }
        this.isNihulPage.set(isNihul);
        if (isNihul) {
            //kill ads that are already showing; leaving nihul, playAds() (runs on every NavigationEnd) restores them
            this.adService.setIsEnableAdStickyFooter(false);
            this.adService.setIsAdTopEnable(false);
            //same for a broadcast-notification prompt that was already up when they navigated in -
            //the guard in _showBroadcastNotificationPromptIfNeeded only stops NEW ones
            this.isBroadCastNotificationEnable.set(false);
        }
        this.changeDetector.markForCheck();
    }

    ngAfterViewInit() {
        this._subscriptions.push(
            this._siteHeaderService.getIsMainMenuOpen().subscribe((isMainMenuOpen: boolean) => {
                this.isMainMenuOpen.set(isMainMenuOpen);
                this.changeDetector.markForCheck();
            }),
            this.adService.getIsEnableAdStickyFooter().subscribe((isEnableAdStickyFooter: boolean) => {
                this.isEnableAdStickyFooter.set(isEnableAdStickyFooter);
                if (isEnableAdStickyFooter) {
                    this.isClosingStickyFooterAd.set(false);
                }
                this.changeDetector.markForCheck();
            }),
            this._scrollingService.scrollAtTop$.subscribe((isAtTop) => {
                this.isAtTop.set(isAtTop);

                this._setHeaderMode();

                if (!isAtTop) {
                    // Ad should never disapear after the first time it goes true.
                    this.isAdBottomMobileEnable.set(true);
                }

                this.changeDetector.markForCheck();
            }),
            this._calendarService.getCurrDayTimesAllData().subscribe((dayTimesAllData) => {
                if (dayTimesAllData) {
                    const { times } = dayTimesAllData.standardTimes;
                    const currPlace = dayTimesAllData.standardTimes.place;
                    const mainDailyTimes = times.filter((time, i: number) => this._calendarService.MAIN_TIMES_MAP[i] || time.modeTime === ModeTime.ClosestNext);
                    const closestTimeDay =
                        mainDailyTimes.find((time) => time.modeTime === ModeTime.ClosestNext) ||
                        mainDailyTimes.find((time) => time.modeTime === ModeTime.ClosestNextMainTime);

                    if (closestTimeDay) {
                        this.dailyTimeHeader.set({ mainDailyTimes, closestTimeDay, currPlace });
                        this.changeDetector.markForCheck();
                    } else {
                        throw new Error('No closest time found in daily times data');
                    }
                }
            }),
        );
        this.subscribeIsFirstScreen();
        this._subscriptions.push(
            this._siteHeaderService.getHeaderData().subscribe({
                next: (data) => {
                    // this.hebrewDate = data.HebrewDate;
                    this.dailyDonor.set(data.DailyDonor || this._siteHeaderService.getEmptyDailyDonor());
                    // this.omer = data.Omer;

                    // Null check to keep opacity in case there is no header data
                    if (data.TextBanner) {
                        this.bannerTop.set({
                            txt: data.TextBanner,
                            link: data.LinkBanner,
                            clr: data.ColorTxtBanner,
                            bgClr: data.BackcolorBanner,
                        });
                    }

                    this.changeDetector.markForCheck();
                },
            }),
        );

        // Subscribe to AllTimes API for omer and hebrewDate
        this._subscriptions.push(
            this._calendarService.getOmer().subscribe((omer: string | null) => {
                this.omer.set(omer || '');
                this.changeDetector.markForCheck();
            }),
        );
        this._subscriptions.push(
            this._calendarService.getHebDateApi().subscribe((hebDate: string | null) => {
                this.hebrewDate.set(hebDate || '');
                this.changeDetector.markForCheck();
            }),
        );

        this._getIsAdTopLoadedTopSub = this.adService.getIsAdTopLoaded().subscribe((isAdLoaded) => {
            setTimeout(() => {
                {
                    this.isAdLoaded.set(isAdLoaded);
                    this._queueHeaderStickyTopSync();
                    this.changeDetector.markForCheck();
                }
            }, 0);
        });
        this._subscriptions.push(this._getIsAdTopLoadedTopSub);
        this._subscriptions.push(
            this.urlAddress
                .getIsAngularApp()
                .pipe(take(1))
                .subscribe((isAngularApp) => {
                    this.isAngularApp.set(isAngularApp);
                    this.changeDetector.markForCheck();
                    if (isAngularApp) {
                        this._subscriptions.push(
                            this.urlAddress.getIsHomeContentArea().subscribe((isHomeContentArea: boolean) => {
                                this.isHomeContentArea.set(isHomeContentArea);
                                this.changeDetector.markForCheck();
                            }),
                        );
                        this._subscriptions.push(
                            this.urlAddress.getActiveAngularPage().subscribe((activeAngularPage: AngularPages) => {
                                this.activeAngularPage.set(activeAngularPage);

                                // Manually call to avoid Error: NG0100: ExpressionChangedAfterItHasBeenCheckedError
                                this.changeDetector.detectChanges();
                            }),
                        );
                    } else {
                        // This is a bit of hack but when outside angular application the above the subscribtion never called.
                        // Outside Angular application we're always defaulted to Inner mode.

                        // Server not generating non angular pages so itr can be excluded safely
                        if (!AppGlobalsModule.isServer) {
                            this.headerMode.set(HeaderMode.Static);

                            // Manually call to avoid Error: NG0100: ExpressionChangedAfterItHasBeenCheckedError
                            this.changeDetector.detectChanges();
                        }
                    }

                    this._subscriptions.push(
                        this.urlAddress.getActiveLocation$().subscribe((activeLocation: SiteLocations) => {
                            setTimeout(() => {
                                {
                                    this.activeLocation.set(activeLocation);
                                    //the first client-side mode comes from here (scroll$ only emits on a scroll)
                                    this._setHeaderMode();
                                    this.changeDetector.detectChanges();
                                    // Manually call to avoid Error: NG0100: ExpressionChangedAfterItHasBeenCheckedError
                                }
                            }, 0);
                        }),
                    );
                    this._subscriptions.push(
                        this.adService.getIsAdTopEnable().subscribe((_isAdTopEnable) => {
                            this.isAdTopEnable.set(_isAdTopEnable);
                            if (_isAdTopEnable) {
                                this._subscriptions.push(
                                    // Scroll needs the current ad position immediately, so keep this path synchronous.
                                    (this._setHeaderStickyTopSub = this._browser.fromWindowEvent('scroll').subscribe((_) => this._setHeaderStickyTop())),
                                );
                            } else {
                                this.adService.setIsAdTopLoaded(false);
                                this._util.unsubscribe([this._setHeaderStickyTopSub]);
                                this.changeDetector.markForCheck();
                            }
                        }),
                    );

                    this._subscribeOnScrollDownForEnableAdStickyFooter();
                }),
        );

        this._subscriptions.push(
            this.adService.getCurrAdTopType().subscribe((currAdTopType) => {
                const innerHeight = this._browser.viewportHeight;
                if (this.breakPointSizeService.isMiniMobileSize() && innerHeight < this.MINI_MOBILE_HEIGHT) {
                    this.currAdTopType.set(AdsType.MediumRectangle);
                } else {
                    this.currAdTopType.set(currAdTopType);
                }
                this._queueHeaderStickyTopSync();
                this.changeDetector.markForCheck();
            }),
        );

        // this.animation.nativeElement.addEventListener('animationend', () => {
        //     this._setHeaderStickyTop();
        // });

        this._subscriptions.push(
            this._siteHeaderService
                .getHeaderMode$()
                .pipe(distinctUntilChanged())
                .subscribe((headerMode) => {
                    this.headerMode.set(headerMode);
                    this.changeDetector.markForCheck();
                }),
        );

        // This is ugly as heck, but not as ugly as using IE11.
        // http://browser-update.org/customize.html
        if (this.isServer) {
            let script = `
            ${/* This will redirect the page to from IE to Edge on windows 10/11*/ ''}
            if(/MSIE \d|Trident.*rv:/.test(navigator.userAgent) && navigator.userAgent.indexOf('Windows NT 10.0') > -1) {
                window.location = 'microsoft-edge:' + window.location;
                setTimeout(function() {
                   //window.open('', '_self', '').close();
                   window.location = 'https://support.microsoft.com/en-us/topic/we-recommend-viewing-this\
                   -website-in-microsoft-edge-160fa918-d581-4932-9e4e-1075c4713595';
                }, 0);
             }
        ${
            /* Depracation announcment for old browsers, basically 3 years and older
        Cause the so called evergreen browsers ain't so green XD  */ ''
        }
        var $buoop = {
            required:{e:-15,c:-15,f:-30,o:-20,s:-4,samsung:-3},
            api:2022.03,
            l: '${this.i18n.getLangshort()}',
            shift_page_down: false,
         };
        function $buo_f(){
         var e = document.createElement("script");
         e.src = "https://browser-update.org/update.min.js";
         document.body.appendChild(e);
        };
        try {document.addEventListener("DOMContentLoaded", $buo_f,false)}
        catch(e){window.attachEvent("onload", $buo_f)}
        `;
            const node = this._doc.createElement('script');
            node.text = script;
            node.type = 'text/javascript';
            node.async = true;
            this._renderer2.appendChild(this._doc.body, node);
        }

        this._hasCompletedInitialHeaderSync = true;
        void this._loadSocialShareBox();
    }

    ngOnDestroy() {
        this._isDestroyed = true;
        this._util.unsubscribe([this._setHeaderStickyTopSub]);
        this._util.unsubscribe(this._subscriptions);
    }

    public closeStickyFooterAd(): void {
        if (this.isClosingStickyFooterAd()) {
            return;
        }

        this.isClosingStickyFooterAd.set(true);
        this.changeDetector.markForCheck();
    }

    public onStickyFooterAdAnimationEnd(event: AnimationEvent): void {
        if (this.isClosingStickyFooterAd() && event.animationName === 'footer-ad-slide-out') {
            this.adService.setIsEnableAdStickyFooter(false);
            this.isClosingStickyFooterAd.set(false);
            this.changeDetector.markForCheck();
        }
    }

    private _setHeaderStickyTop() {
        // Empty ad containers do not reserve sticky space; this also handles blocked or unavailable ads.
        let adTop = this._doc.getElementById('adTop');
        let diff: number;

        // Also in server we always want to stick to the top
        if (this.isServer || (adTop && adTop.offsetHeight === 0)) {
            diff = 0;
        } else {
            // In Iframe there are no headers and thus it's always 0
            diff = adTop ? adTop.offsetHeight : 0;
        }

        const nextIsTopAdAboveScreen = diff <= 0;

        if (this.isTopAdAboveScreen() !== nextIsTopAdAboveScreen) {
            this.isTopAdAboveScreen.set(nextIsTopAdAboveScreen);
            this.changeDetector.markForCheck();
        }
    }

    private _queueHeaderStickyTopSync(): void {
        if (this._isDestroyed) {
            return;
        }

        if (this.isServer) {
            this._setHeaderStickyTop();
            return;
        }

        // Coalesce resize/ad-load/ad-type changes into one post-render DOM read to avoid stale ad heights.
        if (this._hasQueuedHeaderStickyTopSync) {
            return;
        }

        this._hasQueuedHeaderStickyTopSync = true;
        afterNextRender(
            {
                read: () => {
                    this._hasQueuedHeaderStickyTopSync = false;
                    if (this._isDestroyed) {
                        return;
                    }
                    this._setHeaderStickyTop();
                },
            },
            { injector: this._injector },
        );
    }

    private _subscribeOnScrollDownForEnableAdStickyFooter() {
        if (this.isAngularApp()) {
            this._subscriptions.push(
                this._router.events.pipe(filter((e: Event | NavigationEnd): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((_) => this.playAds()),
            );
        } else {
            this.playAds();
        }
    }

    private playAds() {
        //never on the nihul - read the address directly: this runs from its own NavigationEnd
        //subscription and must not depend on the isNihulPage update having run first
        if (SiteHeaderComponent._isNihulUrl(this._router.url) || isAdFreeUrl(this._router.url)) {
            return;
        }
        if (!this.isAdTopEnable()) {
            // We delay the ad a little so it won't hold up the page
            this.globals.setTimeoutIsNotServer(() => {
                this.adService.setIsAdTopEnable(true);
            }, 20);
        }

        // On mobile there is always static footer ad
        if (this._browser.viewportHeight > this.LEPTOP_INNER_HEIGHT) {
            if (!this.isEnableAdStickyFooter()) {
                this._subscriptions.push(
                    this._scrollingService.scrollDown$
                        .pipe(debounceTime(500))
                        .pipe(take(1))
                        .subscribe((_) => {
                            // Do not enable the sticky footer ad if it already showed recently
                            if (!this.adService.CheckAdViewRecently('adStickyFooter')) {
                                this.adService.setIsEnableAdStickyFooter(true);
                            }
                        }),
                );
            }
        }
    }

    private _setHeaderMode(): void {
        if (this.globals.isServer) {
            // In server this value should be defaulted and assign only once per page ( doesn't support scrolling )
            if (this.headerMode()) {
                return;
            }
        }

        if (!this._hasCompletedInitialHeaderSync) {
            if (this._hasQueuedInitialHeaderSync) {
                return;
            }

            this._hasQueuedInitialHeaderSync = true;
            queueMicrotask(() => {
                this._hasQueuedInitialHeaderSync = false;
                this._setHeaderMode();
                this.changeDetector.markForCheck();
            });
            return;
        }

        // Header mode changes can happen during route/location updates, before the ad wrapper has settled.
        this._queueHeaderStickyTopSync();
        this._siteHeaderService.setHeaderMode$(this._siteHeaderService.getHeaderMode(this.isAtTop()));
    }

    private _isFirstScreen$: Subscription | undefined;
    private subscribeIsFirstScreen() {
        this._isFirstScreen$ = this._scrollingService.isFirstScreen$.subscribe((isFirstScreen: boolean) => {
            this.isFirstScreen.set(isFirstScreen);
            this.changeDetector.markForCheck();
        });
        this._subscriptions.push(this._isFirstScreen$);
    }

    public onSearchClick(): void {
        if (
            this.urlAddress.contentAreaRoutes.includes(this.urlAddress.rootAngularUrl) &&
            this.activeAngularPage() !== AngularPages.Filter &&
            this.activeAngularPage() !== AngularPages.Series
        ) {
            this._filterBoxService.toggleFilterBoxOpen();
        } else {
            this.toggleMenu();
        }
    }

    public toggleMenu(): void {
        this._siteHeaderService.setIsMainMenuOpen(!this.isMainMenuOpen());
        this.FocusSearch.set(true);
    }

    public closeMenu(): void {
        this._siteHeaderService.setIsMainMenuOpen(false);
    }

    broadcastNotificationClosed(value: boolean) {
        this.isBroadCastNotificationEnable.set(false);
        this.changeDetector.markForCheck();
    }

    private async _showBroadcastNotificationPromptIfNeeded(isInitialNavigation: boolean): Promise<void> {
        if (this._isDestroyed) {
            return;
        }

        //never in the nihul - it is an internal work tool, and a "מתחברים לתורה" notifications
        //prompt over an editor's screen is the same intrusion the ad suppression exists to avoid.
        //Returning BEFORE recording the navigation is deliberate: nihul navigations must not
        //count toward the every-N-navigations interval either, or a long editing session would
        //fire the prompt the moment the user opens a public page
        if (this.isNihulPage()) {
            return;
        }

        const shouldShowPermissionPrompt = await this.broadcastNotificationService.recordNavigationAndShouldShowPermissionPrompt(isInitialNavigation);

        //re-check after the await: it spans a possible navigation INTO the nihul, and a prompt
        //granted for the public page behind us would open over the editor - permanently, since
        //the entry-time clear in _updateIsNihulPage only runs on the public->nihul flip
        if (this._isDestroyed || this.isNihulPage() || !shouldShowPermissionPrompt) {
            return;
        }

        this.isBroadCastNotificationEnable.set(true);
        void this._loadBroadcastNotification();
        this.changeDetector.markForCheck();
    }

    private async _loadBroadcastNotification(): Promise<void> {
        if (this.broadcastNotificationComponent()) {
            return;
        }

        const { BroadcastNotificationLazyComponent } = await import('./broadcast-notification/broadcast-notification-lazy.component');
        this.broadcastNotificationComponent.set(BroadcastNotificationLazyComponent);
        this.changeDetector.markForCheck();
    }

    private async _loadSocialShareBox(): Promise<void> {
        if (this.socialShareBoxComponent() && this.socialShareBoxModule()) {
            return;
        }

        const [{ SocialShareBoxComponent }, { SocialShareLazyModule }] = await Promise.all([
            import('../shared/social-share/social-share-box/social-share-box.component'),
            import('../shared/social-share/social-share-lazy.module'),
        ]);
        this.socialShareBoxComponent.set(SocialShareBoxComponent);
        this.socialShareBoxModule.set(SocialShareLazyModule);
        this.changeDetector.markForCheck();
    }
}
